/** P9.2 "What moved your value": the bridge as a waterfall and what to watch next year. */
import { useState } from "react";
import type { ContentCatalog, DraftView, RoundReport } from "@bsim/contracts";
import type { ComplexityLevel } from "@bsim/domain";
import { GxWaterfall } from "../charts";
import { GxTermText } from "../theme/GxTerm";
import { formatPp, formatUsdPerShare } from "../format";
import { GxMovedPanel } from "./MovedPanel";
import { bridgeSteps, changedDecisionIds } from "./derive";
import { watchNextYear } from "./story";

/** "What to watch next year" (no card of its own on Results; the solo page keeps it inside "What moved your value"). Null when there is nothing to say. */
export function WatchNextYear({ notes, heading = true }: { readonly notes: readonly string[]; readonly heading?: boolean }) {
  if (notes.length === 0) return null;
  return (
    <div className="gx-results-watch" data-testid="results.watch">
      {heading ? <div className="gx-eyebrow">What to watch next year</div> : null}
      <ul>{notes.map((n) => <li key={n}><GxTermText>{n}</GxTermText></li>)}</ul>
    </div>
  );
}

/** `isFinal`: the last round has no next year to watch, so the note is left out. */
export function Moved({ report, catalog, draft = null, isFinal = false, nextLevel = "executive" }: {
  readonly report: RoundReport; readonly catalog: ContentCatalog; readonly draft?: DraftView | null; readonly isFinal?: boolean;
  /** The complexity of the next round, which decides the levers worth watching (`TeamState.cohort.complexityByRound`). */
  readonly nextLevel?: ComplexityLevel;
}) {
  const [all, setAll] = useState(false);
  const { scorecard, score } = report;
  const steps = bridgeSteps(scorecard.valueBridge, catalog, all, changedDecisionIds(catalog, draft));
  const notes = isFinal ? [] : watchNextYear(report, catalog, draft, nextLevel);
  return (
    <GxMovedPanel aside={scorecard.valueBridge.length > 3 ? <button type="button" className="gx-btn gx-btn--ghost gx-small" aria-pressed={all} onClick={() => setAll((v) => !v)}>{all ? "Show top three" : "Show all"}</button> : null}>
      <GxWaterfall
        steps={steps} zeroCaption="0 = your status-quo twin" summary="How each step added to or took from your value created per share, against your status-quo twin"
        total={{ label: "Value created", value: score.valueCreatedUsdPerShare, valueLabel: `${formatUsdPerShare(score.valueCreatedUsdPerShare, { signed: true })}/share`, detail: `${formatPp(score.valueCreatedRate, { signed: true })} official` }}
      />
      <WatchNextYear notes={notes} />
    </GxMovedPanel>
  );
}
